import { components as c } from "@/components";
import { formComponents as fc } from "@/components/form";
import { Label } from "@/components/ui/label";
import { useClinicianOptions } from "@/features/auth/api/cliniciansByOrganisationQuery";
import InviteForm from "@/features/invite/components/InviteForm";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { isUuid } from "@/services/isUuid";
import { useAuth } from "@/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const shockwaveFormSchema = z.object({
  referring_clinician_id: z.string().uuid().optional().nullable(),
  shockwaving_clinician_id: z.string().uuid().optional().nullable(),
});

type ShockwaveFormValues = z.infer<typeof shockwaveFormSchema>;

const ShockwaveFormFieldsPage = () => {
  const auth = useAuth();

  const {
    referring_clinician_id,
    shockwaving_clinician_id,
    organisation_id,
    setShockwaveReferringClinicianId,
    setShockwavingClinicianId,
  } = useTemplateStore();

  const {
    markStepComplete,
    nextStep,
    setCurrentStep,
    getNextStepRoute,
    getPreviousStepRoute,
    currentStep,
  } = useTemplateNavigationStore();
  const navigate = useNavigate();
  const { templateId } = Route.useParams();

  useEffect(() => {
    setCurrentStep("shockwave");
  }, [setCurrentStep]);

  const form = useForm<ShockwaveFormValues>({
    resolver: zodResolver(shockwaveFormSchema),
    defaultValues: {
      referring_clinician_id: isUuid(referring_clinician_id)
        ? referring_clinician_id
        : auth?.id,
      shockwaving_clinician_id: isUuid(shockwaving_clinician_id)
        ? shockwaving_clinician_id
        : auth?.id,
    },
  });

  const clinicianOptions = useClinicianOptions(organisation_id);

  useEffect(() => {
    console.log(form.watch("shockwaving_clinician_id"), "shockwave clin");
    const subscription = form.watch((value) => {
      setShockwaveReferringClinicianId(
        value?.referring_clinician_id ?? null,
      );
      setShockwavingClinicianId(value?.shockwaving_clinician_id ?? null);
    });
    return () => subscription.unsubscribe();
  }, [
    form,
    setShockwaveReferringClinicianId,
    setShockwavingClinicianId,
  ]);

  const handleNext = () => {
    markStepComplete(currentStep);
    nextStep();
    const nextRoute = getNextStepRoute(currentStep, { templateId });
    if (nextRoute) navigate(nextRoute);
  };

  const handlePrevious = () => {
    const prevRoute = getPreviousStepRoute(currentStep, { templateId });
    if (prevRoute) navigate(prevRoute);
  };

  return (
    <c.ui.Form {...form}>
      <form onSubmit={form.handleSubmit(handleNext)}>
        <div className="space-y-6">
          <div>
            <h2 className="mb-4 font-semibold text-xl">
              Shockwave Treatment
            </h2>
            <p className="mb-6 text-slate-500 dark:text-slate-300">
              Add clinician details for the shockwave treatment
            </p>
          </div>

          <div>
            {/* Referrer */}
            <Label htmlFor="referring_clinician_id">
              Referring Clinician
            </Label>
            <fc.ComboBox
              autoFocus
              name="referring_clinician_id"
              description="The clinician who requests the shockwave treatment"
              options={clinicianOptions}
              clearable={true}
              addOption={<InviteForm />}
            />
          </div>
          <div>
            {/* Shockwave Clinician */}
            <Label htmlFor="shockwaving_clinician_id">
              Shockwave Clinician
            </Label>
            <fc.ComboBox
              name="shockwaving_clinician_id"
              description="The clinician who performs the shockwave treatment"
              options={clinicianOptions}
              clearable={true}
              addOption={<InviteForm />}
            />
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4 mt-8">
          <c.ui.Button
            type="button"
            variant="outline"
            onClick={handlePrevious}
          >
            Previous
          </c.ui.Button>

          <c.ui.Button type="submit">
            Next
          </c.ui.Button>
        </div>
      </form>
    </c.ui.Form>
  );
};

export const Route = createFileRoute(
  "/_app/new/diary/$templateId/events/shockwave/",
)({
  component: ShockwaveFormFieldsPage,
});
